/**
 * Promise-based confirm / alert using dashboard chrome (no window.confirm / alert).
 */
import React, { useCallback, useEffect, useState } from 'react';
import './HomebaseModal.css';

function ModalPresent({ dialog, onResolve }) {
  useEffect(() => {
    if (!dialog) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        onResolve(dialog.mode === 'confirm' ? false : undefined);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dialog, onResolve]);

  if (!dialog) return null;

  const {
    mode,
    title = 'Confirm',
    body,
    warn,
    danger,
    confirmLabel = 'Continue',
    cancelLabel = 'Cancel',
  } = dialog;

  const bodyClass = warn ? 'hb-modal-body hb-modal-body--warn' : 'hb-modal-body';

  return (
    <div
      className="hb-modal-overlay"
      role="presentation"
      onClick={() => mode === 'confirm' && onResolve(false)}
    >
      <div
        className="hb-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="hb-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h4 id="hb-modal-title">{title}</h4>
        <div className={bodyClass}>{typeof body === 'string' ? <p>{body}</p> : body}</div>
        <div className="hb-modal-actions">
          {mode === 'confirm' && (
            <button type="button" className="btn-hb-modal-cancel" onClick={() => onResolve(false)}>
              {cancelLabel}
            </button>
          )}
          <button
            type="button"
            className={
              mode === 'alert'
                ? 'btn-hb-modal-ok'
                : danger
                  ? 'btn-hb-modal-danger'
                  : 'btn-hb-modal-ok'
            }
            onClick={() => onResolve(true)}
          >
            {mode === 'alert' ? 'OK' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function useHomebaseModal() {
  const [dialog, setDialog] = useState(null);

  const onResolve = useCallback((result) => {
    setDialog((d) => {
      if (d?.resolve) {
        if (d.mode === 'alert') {
          d.resolve();
        } else {
          d.resolve(Boolean(result));
        }
      }
      return null;
    });
  }, []);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      setDialog({ ...opts, mode: 'confirm', resolve });
    });
  }, []);

  const alert = useCallback((opts) => {
    return new Promise((resolve) => {
      setDialog({ ...opts, mode: 'alert', resolve });
    });
  }, []);

  const ModalLayer = useCallback(() => <ModalPresent dialog={dialog} onResolve={onResolve} />, [
    dialog,
    onResolve,
  ]);

  return { confirm, alert, ModalLayer };
}
